import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Card, ProgressBar, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useAnalyzeDomainMutation,
  backlinkDomainStatusApi
} from '../api/backlinkDomainStatusApi';
import ApiLoadError from '@/components/errors/ApiLoadError';
import {
  useCentrifugeProgress,
  type CentrifugeProgressMessage
} from '@/hooks/useCentrifugeProgress';
import './backlinkPipelineState.css';

const LINK_PATH = 'M 50 62 C 88 34, 112 34, 150 62';
const HUB = { cx: 100, cy: 46, r: 17 };

type AnalyzeError = {
  error?: string;
  data?: {
    msg?: string;
  };
};

const BacklinkLoadingAnimation = ({ variant = 'primary' }: Record<string, any>) => (
  <div
    className={`backlink-collect-visual backlink-collect-visual--${variant}`}
    aria-hidden="true"
  >
    <div className="backlink-collect-visual__stage">
      <svg
        className="backlink-collect-visual__svg"
        viewBox="0 0 200 108"
        xmlns="http://www.w3.org/2000/svg"
        role="presentation"
      >
        <defs>
          <linearGradient
            id="backlink-collect-flow-gradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.15" />
            <stop offset="45%" stopColor="currentColor" stopOpacity="1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        <rect
          className="backlink-collect-visual__node backlink-collect-visual__node--a"
          x="20"
          y="49"
          width="30"
          height="26"
          rx="6"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="26"
          y1="56"
          x2="42"
          y2="56"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="26"
          y1="62"
          x2="38"
          y2="62"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="26"
          y1="68"
          x2="40"
          y2="68"
        />

        <rect
          className="backlink-collect-visual__node backlink-collect-visual__node--b"
          x="150"
          y="49"
          width="30"
          height="26"
          rx="6"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="156"
          y1="56"
          x2="172"
          y2="56"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="156"
          y1="62"
          x2="168"
          y2="62"
        />
        <line
          className="backlink-collect-visual__node-line"
          x1="156"
          y1="68"
          x2="170"
          y2="68"
        />

        <path className="backlink-collect-visual__track" d={LINK_PATH} />
        <path className="backlink-collect-visual__flow" d={LINK_PATH} />

        <circle className="backlink-collect-visual__traveler" r="4.5">
          <animateMotion
            dur="2.1s"
            repeatCount="indefinite"
            path={LINK_PATH}
            calcMode="spline"
            keyTimes="0;1"
            keySplines="0.42 0 0.58 1"
          />
        </circle>

        <circle
          className="backlink-collect-visual__link-hub-cover"
          cx={HUB.cx}
          cy={HUB.cy}
          r={HUB.r}
        />

      </svg>
      <div className="backlink-collect-visual__link-mark">
        <FontAwesomeIcon icon="link" />
      </div>
    </div>
  </div>
);

const displayHost = url => {
  if (!url) return '';
  try {
    const href = url.startsWith('http') ? url : `https://${url}`;
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return String(url).replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
};

const AnalyzeEmptyState = ({
  status,
  host,
  isAnalyzing,
  analyzeError,
  onAnalyze
}: Record<string, any>) => {
  const isNeedsAnalyze = status === 'needs_analyze';

  return (
    <Card className="border-0 shadow-sm w-100 my-5">
      <Card.Body>
        <div
          className="backlink-pipeline-empty d-flex flex-column align-items-center text-center px-3"
          role="status"
        >
      <div
        className="d-flex align-items-center justify-content-center mb-4 rounded-circle"
        style={{
          width: 72,
          height: 72,
          background:
            'linear-gradient(145deg, rgba(44, 123, 229, 0.12), rgba(44, 123, 229, 0.04))',
          border: '1px solid rgba(44, 123, 229, 0.18)'
        }}
        aria-hidden
      >
        <FontAwesomeIcon
          icon={isNeedsAnalyze ? 'chart-line' : 'link'}
          className="text-primary"
          style={{ fontSize: '1.6rem' }}
        />
      </div>

      <h4 className="fw-semibold text-1000 mb-2" style={{ letterSpacing: '-0.02em' }}>
        {isNeedsAnalyze
          ? 'گزارش بک‌لینک آماده نیست'
          : 'آمادهٔ تحلیل بک‌لینک‌ها'}
      </h4>

      {host ? (
        <p
          className="text-primary fw-medium mb-2 px-3 py-1 rounded-pill"
          style={{
            background: 'rgba(44, 123, 229, 0.08)',
            fontSize: '0.9rem',
            direction: 'ltr'
          }}
        >
          {host}
        </p>
      ) : null}

      <p className="text-600 mb-2" style={{ lineHeight: 1.7, fontSize: '0.95rem' }}>
        {isNeedsAnalyze
          ? 'برای دیدن آمار و لینک‌های ورودی، یک‌بار تحلیل را اجرا کنید. معمولاً چند دقیقه طول می‌کشد.'
          : 'هنوز گزارشی برای این دامنه نداریم. با زدن دکمه زیر، تحلیل و بررسی بک‌لینک‌ها شروع می‌شود.'}
      </p>
      <p className="text-500 mb-4" style={{ lineHeight: 1.7, fontSize: '0.9rem' }}>
        (این فرایند ممکن است بین ۱۰ دقیقه تا ۷ روز طول بکشد.)
      </p>

      <Button
        variant="primary"
        size="lg"
        className="px-4 d-inline-flex align-items-center gap-2 shadow-sm"
        disabled={isAnalyzing || !host}
        onClick={onAnalyze}
        style={{ minWidth: 200, borderRadius: 10 }}
      >
        {isAnalyzing ? (
          <>
            <Spinner animation="border" size="sm" />
            <span>در حال شروع…</span>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon="play" />
            <span>شروع تحلیل</span>
          </>
        )}
      </Button>

      {analyzeError ? (
        <ApiLoadError
          compact
          error={analyzeError}
          isError={Boolean(analyzeError)}
          onRetry={onAnalyze}
          isRetrying={isAnalyzing}
          className="mt-3 w-100"
        />
      ) : (
        <p className="text-500 mt-3 mb-0 small">
          پس از شروع، وضعیت پیشرفت اینجا نمایش داده می‌شود.
        </p>
      )}
        </div>
      </Card.Body>
    </Card>
  );
};

const ProgressState = ({
  status,
  message,
  taskId,
  progressPercentage,
  urlsDone,
  urlsTotal
}: Record<string, any>) => {
  const dispatch = useDispatch();
  const isBuilding = status === 'building';
  const active = status === 'collecting' || status === 'building';
  const snapshotPercent =
    typeof progressPercentage === 'number' ? progressPercentage : 0;
  const snapshotDone = typeof urlsDone === 'number' ? urlsDone : 0;
  const snapshotTotal = typeof urlsTotal === 'number' ? urlsTotal : 0;

  const { progressData } = useCentrifugeProgress({
    channelId: taskId || undefined,
    active: Boolean(active && taskId),
    onComplete: () => {
      dispatch(backlinkDomainStatusApi.util.invalidateTags(['DomainStatus']));
    }
  });

  const live = progressFromSocket(progressData);
  const socketWins =
    live != null && live.percent > snapshotPercent ? live : null;
  const percent = socketWins ? socketWins.percent : snapshotPercent;
  const done =
    socketWins && socketWins.processed != null
      ? socketWins.processed
      : snapshotDone;
  const total =
    socketWins && socketWins.total != null ? socketWins.total : snapshotTotal;
  const indeterminate = status === 'collecting' && total <= 0 && percent === 0;

  return (
    <Card className="border-0 shadow-sm w-100 my-5">
      <Card.Body>
        <div
          className="d-flex flex-column align-items-center text-center mx-auto px-3"
          style={{ maxWidth: 620 }}
          role="status"
          aria-live="polite"
        >
          <div className="backlink-pipeline-progress__visual">
            <BacklinkLoadingAnimation variant="primary" />
          </div>
          <h4 className="fw-semibold text-1000 mb-2">
            {isBuilding
              ? 'در حال آماده‌سازی گزارش'
              : 'در حال تحلیل و اعتبارسنجی لینک‌ها'}
          </h4>
          <p className="text-600 mb-3" style={{ lineHeight: 1.7 }}>
            {isBuilding
              ? message || 'داده‌ها جمع شده‌اند؛ گزارش در حال ساخت است…'
              : 'لینک‌های شناسایی‌شده در حال بررسی هستند تا وضعیت فعلی آن‌ها، از جمله لینک‌های حذف‌شده یا تغییرکرده، به‌روزرسانی شود.'}
          </p>
          <div className="w-100 mb-3">
            {indeterminate ? (
              <>
                <div className="d-flex justify-content-between mb-1 small text-600">
                  <span>{(0).toLocaleString('fa-IR')}٪</span>
                </div>
                <div
                  className="backlink-pipeline-progress__track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={0}
                />
              </>
            ) : (
              <>
                <div className="d-flex justify-content-between mb-1 small text-600">
                  <span>{percent.toLocaleString('fa-IR')}٪</span>
                  {total > 0 ? (
                    <span>
                      {done.toLocaleString('fa-IR')} از {total.toLocaleString('fa-IR')}
                    </span>
                  ) : null}
                </div>
                <ProgressBar
                  now={percent}
                  max={100}
                  style={{ height: 6 }}
                  className="rounded-pill"
                  variant="success"
                />
              </>
            )}
          </div>
          <p className="text-500 mb-0" style={{ lineHeight: 1.7, fontSize: '0.9rem' }}>
            (این فرایند ممکن است بین ۱۰ دقیقه تا ۷ روز طول بکشد.)
          </p>
        </div>
      </Card.Body>
    </Card>
  );
};

function progressFromSocket(data: CentrifugeProgressMessage | null) {
  if (!data || typeof data.progress_percentage !== 'number') return null;
  return {
    percent: Math.min(100, Math.max(0, Math.floor(data.progress_percentage))),
    processed: typeof data.processed === 'number' ? data.processed : undefined,
    total: typeof data.total === 'number' ? data.total : undefined
  };
}

/**
 * Empty / progress surface for product MV pipeline.
 * not_registered + needs_analyze → CTA to POST /domain/analyze.
 */
const BacklinkPipelineState = ({
  status,
  message,
  isLoading,
  url,
  domain_slug,
  taskId,
  progressPercentage,
  urlsDone,
  urlsTotal,
  onAnalyzed
}: Record<string, any>) => {
  const [analyzeDomain, { isLoading: isAnalyzing }] =
    useAnalyzeDomainMutation();
  const [analyzeError, setAnalyzeError] = useState<AnalyzeError | null>(null);
  const host = displayHost(url);

  if (isLoading) {
    return (
      <span
        className="visually-hidden"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        در حال بررسی وضعیت دامنه…
      </span>
    );
  }

  const canStartAnalyze =
    status === 'not_registered' || status === 'needs_analyze';

  if (canStartAnalyze) {
    const handleAnalyze = async () => {
      if (!url || !domain_slug) {
        setAnalyzeError({ error: 'آدرس دامنه یا شناسه دامنه ناقص است.' });
        return;
      }
      setAnalyzeError(null);
      try {
        const result = await analyzeDomain({ url, domain_slug }).unwrap();
        onAnalyzed?.(result);
      } catch (err) {
        const errorDetails =
          err && typeof err === 'object' ? (err as AnalyzeError) : null;
        const msg =
          errorDetails?.data?.msg ||
          errorDetails?.error ||
          'شروع تحلیل ناموفق بود. دوباره تلاش کنید.';
        setAnalyzeError({ error: msg });
      }
    };

    return (
      <AnalyzeEmptyState
        status={status}
        host={host}
        isAnalyzing={isAnalyzing}
        analyzeError={analyzeError}
        onAnalyze={handleAnalyze}
      />
    );
  }

  return (
    <ProgressState
      status={status}
      message={message}
      taskId={taskId}
      progressPercentage={progressPercentage}
      urlsDone={urlsDone}
      urlsTotal={urlsTotal}
    />
  );
};

export default BacklinkPipelineState;
